import { writable, derived } from "svelte/store";

interface ActiveJob {
  id: string;
  endpoint: string;
  startTime: number;
  status: "queued" | "processing" | "completed" | "failed";
}

const activeJobs = writable<Map<string, ActiveJob>>(new Map());
const lastJobSuccess = writable<boolean | null>(null);

export const jobTracker = {
  subscribe: activeJobs.subscribe,

  // Derived store that tells if ANY job is processing
  isLoading: derived(activeJobs, ($jobs) => {
    return Array.from($jobs.values()).some(job => job.status === "processing");
  }),

  // Get count of active jobs
  count: derived(activeJobs, ($jobs) => $jobs.size),

  // Get count of processing jobs
  processingCount: derived(activeJobs, ($jobs) => {
    return Array.from($jobs.values()).filter(job => job.status === "processing").length;
  }),

  // Last job success status
  lastJobSuccess: {
    subscribe: lastJobSuccess.subscribe,
  },

  add(jobId: string, endpoint: string) {
    activeJobs.update((jobs) => {
      jobs.set(jobId, { 
        id: jobId, 
        endpoint, 
        startTime: Date.now(),
        status: "queued" 
      });
      return new Map(jobs);
    });
  },

  updateStatus(jobId: string, status: "queued" | "processing" | "completed" | "failed") {
    activeJobs.update((jobs) => {
      const job = jobs.get(jobId);
      if (job) {
        job.status = status;
      }
      return new Map(jobs);
    });
  },

  setSuccess(success: boolean) {
    lastJobSuccess.set(success);
  },

  clearSuccess() {
    lastJobSuccess.set(null);
  },

  remove(jobId: string) {
    activeJobs.update((jobs) => {
      jobs.delete(jobId);
      return new Map(jobs);
    });
  },

  clear() {
    activeJobs.set(new Map());
    lastJobSuccess.set(null);
  },
};
